import { useEffect, useState, type ReactNode } from 'react'
import { type IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'

/**
 * AuthProvider wraps the React tree with `MsalProvider`.
 *
 * Responsibilities:
 *   - Ensure `msalInstance.initialize()` has resolved before children
 *     render. MSAL v3 throws if you call any API before initialize();
 *     `main.tsx` kicks off `initialize()` synchronously, but for tests
 *     and SSR-tolerance we defensively await it again here.
 *   - Process the redirect-response on first mount (so the SPA picks up
 *     a fresh sign-in from `/sign-in/callback` without the page-level
 *     handler having to call `handleRedirectPromise()` separately).
 *
 * Why a wrapper exists at all (rather than `<MsalProvider>` inline in
 * `main.tsx`): isolating this seam keeps the `useAuth()` hook trivially
 * mockable in tests - render-with-auth provides its own stub instance.
 */
export interface AuthProviderProps {
  readonly instance: IPublicClientApplication
  readonly children: ReactNode
}

export function AuthProvider({ instance, children }: AuthProviderProps): JSX.Element {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        await instance.initialize()
        // Process any pending redirect response (sign-in callback path).
        // `handleRedirectPromise` resolves to null on non-callback loads;
        // on the callback load it sets the active account.
        const result = await instance.handleRedirectPromise()
        if (result?.account) {
          instance.setActiveAccount(result.account)
        } else {
          // Ensure an active account is set if MSAL has any cached account.
          const accounts = instance.getAllAccounts()
          if (accounts.length > 0 && !instance.getActiveAccount()) {
            instance.setActiveAccount(accounts[0] ?? null)
          }
        }
      } catch {
        // MSAL surfaces redirect errors via the event API; the page-level
        // sign-in-callback handler renders the error UI. Initialise should
        // never throw on a clean load - if it does, the route guard will
        // route the user to /error on first navigation.
      } finally {
        if (!cancelled) setReady(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [instance])

  if (!ready) {
    // Quiet placeholder - matches the AppShell background so there is no
    // flash of unstyled content. No PII / no controls -> nothing for
    // axe-core to flag.
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Loading Murmuration"
        className="flex h-full items-center justify-center bg-bg-app text-text-muted"
      >
        <span className="sr-only">Loading Murmuration</span>
      </div>
    )
  }

  return <MsalProvider instance={instance}>{children}</MsalProvider>
}
